import { useState } from 'react';
import { playSuccessDing, prepareSuccessDing } from '../../../successDing';

const ModernEditApplicationPopup = ({job, listNames, closePopup, onApplicationUpdated}) => {

    const originalCompanyName = job.companyName;
    const [companyName, setCompanyName] = useState(job.companyName || "");
    const [position, setPosition] = useState(job.position);
    const [location, setLocation] = useState(job.location);
    const [workplaceType, setWorkplaceType] = useState(job.workplaceType || "");
    const [jobLink, setJobLink] = useState(job.jobLink);
    const [list, setList] = useState(job.list);
    const [locationValidationError, setLocationValidationError] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState("");

    const validateLocationFormat = (loc) => {
        if (!loc || loc.trim() === '') return false;
        const normalized = loc.trim().toLowerCase();
        if (normalized === 'remote') return true;
        if (normalized === 'united states') return true;
        const locationRegex = /^.+,\s*[a-z]{2}$/i;
        return locationRegex.test(loc);
    };

    const handleLocationChange = (newLoc) => {
        setLocation(newLoc);
        if (newLoc.trim() === '') {
            setLocationValidationError('Location is required');
        } else if (!validateLocationFormat(newLoc)) {
            setLocationValidationError('Format: Remote, United States, or City, XX (case-insensitive)');
        } else {
            setLocationValidationError('');
        }
    };

    const trimmedCompanyName = companyName.trim();
    const companyNameChanged = trimmedCompanyName !== originalCompanyName;
    const companyNameOk = trimmedCompanyName.length > 0;
    const locationChanged = location !== job.location;
    const workplaceChanged = workplaceType !== (job.workplaceType || "");
    const listChanged = list !== job.list;
    // Only enforce location format when the user edits Location — don't block List-only saves
    // for older/legacy location values that no longer match the current rules.
    const isLocationOk = !locationChanged || validateLocationFormat(location);
    const hasChanges =
        companyNameOk &&
        (companyNameChanged ||
        position !== job.position ||
        locationChanged ||
        workplaceChanged ||
        jobLink !== job.jobLink ||
        listChanged) && isLocationOk;

    const buildDateAppliedValue = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const microseconds = `${String(now.getMilliseconds()).padStart(3, '0')}000`;

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${microseconds}`;
    };

    const buildPatchBody = () => {
        const body = {};

        if (companyNameChanged) body.companyName = trimmedCompanyName;
        if (position !== job.position) body.position = position;
        if (locationChanged) body.location = location;
        if (workplaceChanged) body.workplaceType = workplaceType;
        if (jobLink !== job.jobLink) {
            body.jobLink = jobLink;
            // New job link means re-applied — refresh dateApplied
            body.dateApplied = buildDateAppliedValue();
        }
        if (listChanged) body.list = list;

        return body;
    };

    async function editApplication() {
        if (!hasChanges) return;

        setIsSaving(true);
        setSaveStatus('Saving...');
        prepareSuccessDing();
        const stage = "https://ax00jgr5uf.execute-api.us-east-1.amazonaws.com/dev";
        const url = `${stage}/Jobs/update?username=${encodeURIComponent(localStorage.getItem('username') || '')}&companyName=${encodeURIComponent(originalCompanyName)}`;

        const params = buildPatchBody();

        try {
            const res = await fetch(url, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(params)
            });

            if (!res.ok) {
                throw new Error(`Update failed (${res.status})`);
            }

            const data = await res.json().catch(() => ({}));
            console.log("SUCCESS:", data);
            setSaveStatus('Success!');
            playSuccessDing();
            const updatedJob = { ...job, ...params, companyName: trimmedCompanyName };
            setTimeout(() => {
                if (typeof onApplicationUpdated === 'function') {
                    onApplicationUpdated(updatedJob, { previousCompanyName: originalCompanyName });
                }
                closePopup();
            }, 800);
            return;
        } catch (err) {
            console.error("ERROR:", err);
            setSaveStatus('Failed to save. Please try again.');
            setIsSaving(false);
            return;
        }
    }

    return (
        <div style={{
            position: "fixed", inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex", justifyContent: "center", alignItems: "center",
            zIndex: 9999,
        }}>
            <div
                style={{
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    borderRadius: "16px",
                    padding: "36px 32px 28px",
                    width: "520px",
                    maxWidth: "95vw",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0px",
                    color: "#f0f0f0",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                }}
            >
                {/* Title */}
                <h2 style={{ margin: "0 0 12px", fontSize: "26px", fontWeight: "700", color: "#ffffff" }}>
                    Edit Application
                </h2>

                {/* Subtitle */}
                <p style={{ margin: "0 0 20px", fontSize: "14px", color: "#a0a0a0" }}>
                    Update the details for {originalCompanyName}
                </p>

                {/* Divider */}
                <hr style={{ border: "none", borderTop: "1px solid #333", margin: "0 0 20px" }} />

                {/* Form Fields */}
                <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginBottom: "20px" }}>
                    <div>
                        <label style={{ fontSize: "14px", fontWeight: "600", color: "#f0f0f0", display: "block", marginBottom: "8px" }}>
                            Company Name
                        </label>
                        <input
                            placeholder="Enter company name..."
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "12px 14px",
                                borderRadius: "8px",
                                border: "1px solid #3a3a3c",
                                backgroundColor: "#2c2c2e",
                                color: "#f0f0f0",
                                fontSize: "14px",
                                outline: "none",
                                boxSizing: "border-box",
                            }}
                        />
                    </div>

                    {/* Position */}
                    <div>
                        <label style={{ fontSize: "14px", fontWeight: "600", color: "#f0f0f0", display: "block", marginBottom: "8px" }}>
                            Position
                        </label>
                        <input
                            placeholder="Enter position..."
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "12px 14px",
                                borderRadius: "8px",
                                border: "1px solid #3a3a3c",
                                backgroundColor: "#2c2c2e",
                                color: "#f0f0f0",
                                fontSize: "14px",
                                outline: "none",
                                boxSizing: "border-box",
                            }}
                        />
                    </div>

                    {/* Location */}
                    <div>
                        <label style={{ fontSize: "14px", fontWeight: "600", color: "#f0f0f0", display: "block", marginBottom: "8px" }}>
                            Location / Remote
                        </label>
                        <input
                            placeholder="Remote, United States, or City, XX..."
                            value={location}
                            onChange={(e) => handleLocationChange(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "12px 14px",
                                borderRadius: "8px",
                                border: "1px solid #3a3a3c",
                                backgroundColor: "#2c2c2e",
                                color: "#f0f0f0",
                                fontSize: "14px",
                                outline: "none",
                                boxSizing: "border-box",
                            }}
                        />
                        {locationValidationError && (
                            <span style={{
                                fontSize: '12px',
                                color: '#ffffff',
                                backgroundColor: 'rgba(204, 0, 0, 0.2)',
                                border: '1px solid rgba(204, 0, 0, 0.45)',
                                padding: '6px 10px',
                                borderRadius: '8px',
                                marginTop: '6px',
                                display: 'block'
                            }}>
                                {locationValidationError}
                            </span>
                        )}
                    </div>

                    <div>
                        <label style={{ fontSize: "14px", fontWeight: "600", color: "#f0f0f0", display: "block", marginBottom: "8px" }}>
                            Workplace
                        </label>
                        <select
                            value={workplaceType}
                            onChange={(e) => setWorkplaceType(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "12px 14px",
                                borderRadius: "8px",
                                border: "1px solid #3a3a3c",
                                backgroundColor: "#2c2c2e",
                                color: "#f0f0f0",
                                fontSize: "14px",
                                outline: "none",
                                boxSizing: "border-box",
                                cursor: "pointer",
                            }}
                        >
                            <option value="">Not set</option>
                            <option value="Remote">Remote</option>
                            <option value="Hybrid">Hybrid</option>
                            <option value="On-Site">On-Site</option>
                        </select>
                    </div>

                    {/* Job Link */}
                    <div>
                        <label style={{ fontSize: "14px", fontWeight: "600", color: "#f0f0f0", display: "block", marginBottom: "8px" }}>
                            Job Link
                        </label>
                        <input
                            placeholder="Enter job link..."
                            value={jobLink}
                            onChange={(e) => setJobLink(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "12px 14px",
                                borderRadius: "8px",
                                border: "1px solid #3a3a3c",
                                backgroundColor: "#2c2c2e",
                                color: "#f0f0f0",
                                fontSize: "14px",
                                outline: "none",
                                boxSizing: "border-box",
                            }}
                        />
                    </div>

                    {/* List Dropdown */}
                    <div>
                        <label style={{ fontSize: "14px", fontWeight: "600", color: "#f0f0f0", display: "block", marginBottom: "8px" }}>
                            List
                        </label>
                        <select
                            value={list}
                            onChange={(e) => setList(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "12px 14px",
                                borderRadius: "8px",
                                border: "1px solid #3a3a3c",
                                backgroundColor: "#2c2c2e",
                                color: "#f0f0f0",
                                fontSize: "14px",
                                outline: "none",
                                boxSizing: "border-box",
                                cursor: "pointer",
                            }}
                        >
                            <option value="" disabled> Select a list... </option>
                            {listNames?.map((listName, index) => (
                                <option key={index} value={listName}> {listName} </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Divider */}
                <hr style={{ border: "none", borderTop: "1px solid #333", margin: "0 0 20px" }} />

                {/* Status Message */}
                {saveStatus && (
                    <div style={{ marginBottom: '20px' }}>
                        <span style={{
                            fontSize: '14px',
                            color: saveStatus.includes('Failed') ? '#ff6b6b' : '#00b894',
                            backgroundColor: saveStatus.includes('Failed') ? 'rgba(255, 107, 107, 0.15)' : 'rgba(0, 184, 148, 0.15)',
                            border: saveStatus.includes('Failed') ? '1px solid rgba(255, 107, 107, 0.4)' : '1px solid rgba(0, 184, 148, 0.4)',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            display: 'block',
                            textAlign: 'center'
                        }}>
                            {saveStatus}
                        </span>
                    </div>
                )}

                {/* Buttons */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", alignItems: "flex-start" }}>
                    <button
                        onClick={closePopup}
                        style={{
                            padding: "10px 22px",
                            borderRadius: "8px",
                            border: "1px solid #444",
                            backgroundColor: "#2c2c2e",
                            color: "#f0f0f0",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "background-color 0.2s"
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#3a3a3c"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#2c2c2e"}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={editApplication}
                        disabled={!hasChanges || isSaving}
                        style={{
                            padding: "10px 22px",
                            borderRadius: "8px",
                            border: "none",
                            backgroundColor: hasChanges && !isSaving ? "#4a9eff" : "#3a5080",
                            color: hasChanges && !isSaving ? "#ffffff" : "#7a9aaa",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: hasChanges && !isSaving ? "pointer" : "not-allowed",
                            transition: "background-color 0.2s"
                        }}
                        onMouseEnter={(e) => {
                            if (hasChanges && !isSaving) e.target.style.backgroundColor = "#3a8eef";
                        }}
                        onMouseLeave={(e) => {
                            if (hasChanges && !isSaving) e.target.style.backgroundColor = "#4a9eff";
                        }}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModernEditApplicationPopup;

